// @ts-check
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import Index from '.';
import History from './history';
import { baseLink, getStepLinks } from '../_config';
import NotFound from './404';
import { ProductExternalIdRoutes as ExternalId } from './productExternalIdImport/_config';
import { ProductInternalIdRoutes as InternalId } from './productInternalIdImport/_config';
import MasterListImport from './productMasterListImport/_config';
import MasterListSync from './productMasterListSync/_config';
import Alias from './partnerAliasImport/_config';
import ProductDataImport from './productDataImport/_config';
import ProfileDataImport from './profileDataImport/_config';
import ProductImport from './productImport/_config';
import ProductAllInOneImport from './productAllInOneImport/_config';
import PortfolioProductExternalIdImport from './portfolioProductExternalIdImport/_config';
import ElabelsUpdateImport from './elabelsUpdateImport/_config';
import ProductCreationImport from './productCreationImport/_config';
import ProductListImport from './productListImport/_config';
import { useRedirectHelper } from '../RedirectHelper';

export function ImportsRoutes() {
  useRedirectHelper();
  return (
    <Switch>
      <Route
        path="/:workspaceId/productMasterListImport"
        component={MasterListImport}
      />
      <Route
        path="/:workspaceId/productMasterListSync"
        component={MasterListSync}
      />
      <Route
        path="/:workspaceId/productDataImport"
        component={ProductDataImport}
      />
      <Route
        path="/:workspaceId/profileDataImport"
        component={ProfileDataImport}
      />
      <Route path="/:workspaceId/productImport" component={ProductImport} />
      <Route
        path="/:workspaceId/productAllInOneImport"
        component={ProductAllInOneImport}
      />
      <Route
        path="/:workspaceId/portfolioProductExternalIdImport"
        component={PortfolioProductExternalIdImport}
      />
      <Route
        path="/:workspaceId/elabelsUpdateImport"
        component={ElabelsUpdateImport}
      />
      <Route
        path="/:workspaceId/productCreationImport"
        component={ProductCreationImport}
      />
      <Route
        path="/:workspaceId/productExternalIdImport"
        component={ExternalId}
      />
      <Route
        path="/:workspaceId/productInternalIdImport"
        component={InternalId}
      />
      <Route
        path="/:workspaceId/productListImport"
        component={ProductListImport}
      />
      <Route path="/:workspaceId/partnerAliasImport" component={Alias} />
      {/* /add was the old categorized-grid intermediate page — redirect any
          lingering bookmarks back to the landing picker. */}
      <Redirect from="/:workspaceId/add/:importType" to="/:workspaceId/:importType" />
      <Redirect from="/:workspaceId/add" to="/:workspaceId" />

      <Route
        path="/:workspaceId/history/:status"
        exact
        component={History}
      />
      <Redirect
        from="/:workspaceId/history"
        to="/:workspaceId/history/completed"
      />
      {/* Backwards-compat: old bare /:workspaceId/(inProgress|completed|failed) URLs */}
      <Redirect
        from="/:workspaceId/inProgress"
        to="/:workspaceId/history/inProgress"
      />
      <Redirect
        from="/:workspaceId/completed"
        to="/:workspaceId/history/completed"
      />
      <Redirect
        from="/:workspaceId/failed"
        to="/:workspaceId/history/failed"
      />

      <Route path="/:workspaceId" exact component={Index} />
      <Route component={NotFound} />
    </Switch>
  );
}

/** @typedef {'productExternalIdImport' | 'productMasterListImport' | 'partnerAliasImport' | 'productMasterListSync' | 'productDataImport' | 'productImport'} DataImportType  The possible import types. */
/** @typedef {keyof ReturnType<getCommonLinks>} Step  The possible common steps. */

export function useCommonLinks() {
  /** @type {{workspaceId: string}} */
  const { workspaceId } = useParams();
  const baseLink = `/${workspaceId}`;
  const links = {
    root: () => `${baseLink}`,
    importsInProgress: () => `${baseLink}/history/inProgress`,
    importsCompleted: () => `${baseLink}/history/completed`,
    importsFailed: () => `${baseLink}/history/failed`,
    add: () => `${baseLink}/add`,
    /** @param {{ importType: DataImportType }} options */
    importType: ({ importType }) => `${baseLink}/${importType}`,
    /** @param {{ importType: DataImportType, importId: string }} options */
    import: ({ importType, importId }) =>
      `${baseLink}/${importType}/${importId}`,
    sendNotification: ({ importType, importId }) =>
      `${baseLink}/${importType}/${importId}/sendNotification`,
  };
  return links;
}

/**
 * @param {Step} currentStep
 * @returns
 */
export function useLink(currentStep) {
  const urls = useCommonLinks();
  return getStepLinks(urls, Object.keys(urls), currentStep);
}
